import { useState } from "react";

interface Community {
  name: string;
  route: string;
}

interface Subregion {
  name: string;
  description: string;
  communities: Community[];
  hub: { name: string; route: string };
}

interface Region {
  id: string;
  name: string;
  icon: string;
  subregions: Subregion[];
}

const regions: Region[] = [
  {
    id: "dallas-ft-worth",
    name: "DFW Metroplex",
    icon: "ri-building-line",
    subregions: [
      {
        name: "Dallas Core & Uptown",
        description: "Downtown Dallas, Uptown, Oak Lawn, Deep Ellum, and Knox-Henderson — all within the 635 loop and served from the Dallas hub. Uber/Lyft access to DFW International takes 30–45 minutes.",
        hub: { name: "Dallas", route: "/dallas" },
        communities: [
          { name: "University Park", route: "/dallas" }, { name: "Highland Park", route: "/dallas" },
          { name: "Addison", route: "/dallas" }, { name: "Farmers Branch", route: "/dallas" },
          { name: "Coppell", route: "/dallas" }, { name: "Grapevine", route: "/dallas" },
          { name: "Las Colinas", route: "/dallas" }, { name: "Mesquite", route: "/dallas" },
          { name: "Balch Springs", route: "/dallas" }, { name: "Sunnyvale", route: "/dallas" },
        ],
      },
      {
        name: "North Dallas Suburbs",
        description: "Collin County tech corridor — Plano, Allen, McKinney, and Frisco. All cities are 30–45 minutes north of Downtown Dallas on US-75 or the Dallas North Tollway.",
        hub: { name: "Plano", route: "/plano" },
        communities: [
          { name: "Allen", route: "/plano" }, { name: "Wylie", route: "/plano" },
          { name: "Murphy", route: "/plano" }, { name: "Prosper", route: "/plano" },
          { name: "Celina", route: "/plano" }, { name: "Melissa", route: "/plano" },
          { name: "Lucas", route: "/plano" }, { name: "Parker", route: "/plano" },
          { name: "Sachse", route: "/plano" }, { name: "Rowlett", route: "/plano" },
        ],
      },
      {
        name: "Mid-Cities & Denton County",
        description: "HEB area, Euless, Bedford, and Denton County suburbs along SH-121. DFW Airport is literally within this corridor — critical for airport-adjacent passport needs.",
        hub: { name: "Fort Worth", route: "/fort-worth" },
        communities: [
          { name: "Euless", route: "/fort-worth" }, { name: "Bedford", route: "/fort-worth" },
          { name: "Hurst", route: "/fort-worth" }, { name: "Colleyville", route: "/fort-worth" },
          { name: "Southlake", route: "/fort-worth" }, { name: "Flower Mound", route: "/fort-worth" },
          { name: "Lewisville", route: "/fort-worth" }, { name: "Little Elm", route: "/fort-worth" },
          { name: "The Colony", route: "/fort-worth" }, { name: "Corinth", route: "/fort-worth" },
        ],
      },
      {
        name: "Southwest Tarrant & Johnson County",
        description: "Arlington, Mansfield, Burleson, and Cleburne — south of I-20 on US-287. Johnson County travelers often route through the Fort Worth hub for fastest service.",
        hub: { name: "Arlington", route: "/arlington" },
        communities: [
          { name: "Mansfield", route: "/arlington" }, { name: "Kennedale", route: "/arlington" },
          { name: "Burleson", route: "/arlington" }, { name: "Cleburne", route: "/arlington" },
          { name: "Alvarado", route: "/arlington" }, { name: "Grand Prairie", route: "/arlington" },
          { name: "Midlothian", route: "/arlington" }, { name: "Waxahachie", route: "/arlington" },
          { name: "Cedar Hill", route: "/arlington" }, { name: "Duncanville", route: "/arlington" },
        ],
      },
    ],
  },
  {
    id: "houston-metro",
    name: "Houston Metro",
    icon: "ri-map-pin-2-line",
    subregions: [
      {
        name: "Houston Inner Loop",
        description: "Midtown, Montrose, Heights, Medical Center, and Galleria — all reachable within 30 minutes of Downtown Houston. IAH is about 25 miles north on I-45; Hobby is 10 miles southeast.",
        hub: { name: "Houston", route: "/houston" },
        communities: [
          { name: "Bellaire", route: "/houston" }, { name: "West University Place", route: "/houston" },
          { name: "Pearland", route: "/houston" }, { name: "Missouri City", route: "/houston" },
          { name: "Sugar Land", route: "/houston" }, { name: "Richmond", route: "/houston" },
          { name: "Stafford", route: "/houston" }, { name: "Jersey Village", route: "/houston" },
          { name: "Humble", route: "/houston" }, { name: "Atascocita", route: "/houston" },
        ],
      },
      {
        name: "Northwest Houston & Katy",
        description: "Katy and its master-planned communities sit 30 miles west on I-10. Cy-Fair, Spring, and Klein are served by The Woodlands or Houston hubs depending on direction of travel.",
        hub: { name: "Houston", route: "/houston" },
        communities: [
          { name: "Katy", route: "/houston" }, { name: "Cypress", route: "/houston" },
          { name: "Spring", route: "/houston" }, { name: "Klein", route: "/houston" },
          { name: "Tomball", route: "/houston" }, { name: "Magnolia", route: "/houston" },
          { name: "The Woodlands", route: "/houston" }, { name: "Conroe", route: "/houston" },
          { name: "Friendswood", route: "/houston" }, { name: "League City", route: "/houston" },
        ],
      },
      {
        name: "Southeast Houston & Galveston County",
        description: "Pasadena, Baytown, and Galveston Island — Port of Galveston is a major cruise embarkation port, making expedited passport service critical for last-minute cruise prep.",
        hub: { name: "Houston", route: "/houston" },
        communities: [
          { name: "Galveston", route: "/houston" }, { name: "Texas City", route: "/houston" },
          { name: "La Marque", route: "/houston" }, { name: "League City", route: "/houston" },
          { name: "Baytown", route: "/houston" }, { name: "La Porte", route: "/houston" },
          { name: "Deer Park", route: "/houston" }, { name: "South Houston", route: "/houston" },
          { name: "Alvin", route: "/houston" }, { name: "Manvel", route: "/houston" },
        ],
      },
    ],
  },
  {
    id: "austin-san-antonio",
    name: "Austin & San Antonio Corridor",
    icon: "ri-landscape-line",
    subregions: [
      {
        name: "Austin Metro",
        description: "Travis County and its suburbs form Central Texas's largest metro. Austin-Bergstrom International (ABIA) is on the southeast side. Round Rock, Cedar Park, and Georgetown are 20–30 minutes north on I-35.",
        hub: { name: "Austin", route: "/austin" },
        communities: [
          { name: "Round Rock", route: "/austin" }, { name: "Cedar Park", route: "/austin" },
          { name: "Georgetown", route: "/austin" }, { name: "Leander", route: "/austin" },
          { name: "Pflugerville", route: "/austin" }, { name: "Hutto", route: "/austin" },
          { name: "Kyle", route: "/austin" }, { name: "Buda", route: "/austin" },
          { name: "Bastrop", route: "/austin" }, { name: "Manor", route: "/austin" },
        ],
      },
      {
        name: "San Antonio & Bexar County",
        description: "San Antonio International Airport (SAT) is 10 minutes north of Downtown. Schertz, Converse, and Universal City cluster northeast off I-35; New Braunfels is 30 minutes north.",
        hub: { name: "San Antonio", route: "/san-antonio" },
        communities: [
          { name: "New Braunfels", route: "/san-antonio" }, { name: "Schertz", route: "/san-antonio" },
          { name: "Cibolo", route: "/san-antonio" }, { name: "Universal City", route: "/san-antonio" },
          { name: "Converse", route: "/san-antonio" }, { name: "Helotes", route: "/san-antonio" },
          { name: "Leon Valley", route: "/san-antonio" }, { name: "Boerne", route: "/san-antonio" },
          { name: "Seguin", route: "/san-antonio" }, { name: "Floresville", route: "/san-antonio" },
        ],
      },
      {
        name: "I-35 Corridor — Killeen to San Marcos",
        description: "The mid-Texas I-35 corridor includes Waco, Killeen (Fort Cavazos), Temple, and San Marcos. Killeen-Fort Hood Regional Airport serves the large military community needing fast passport processing.",
        hub: { name: "Waco", route: "/waco" },
        communities: [
          { name: "Killeen", route: "/killeen" }, { name: "Copperas Cove", route: "/killeen" },
          { name: "Harker Heights", route: "/killeen" }, { name: "Temple", route: "/waco" },
          { name: "Belton", route: "/waco" }, { name: "Hewitt", route: "/waco" },
          { name: "Woodway", route: "/waco" }, { name: "San Marcos", route: "/austin" },
          { name: "Wimberley", route: "/austin" }, { name: "Lockhart", route: "/austin" },
        ],
      },
    ],
  },
  {
    id: "west-texas",
    name: "West Texas & El Paso",
    icon: "ri-sun-line",
    subregions: [
      {
        name: "El Paso & Borderland",
        description: "El Paso International Airport is one of the closest US airports to Mexico. Socorro, Horizon City, and Anthony span both sides of the New Mexico state line. Cross-border travel makes passport urgency especially high here.",
        hub: { name: "El Paso", route: "/el-paso" },
        communities: [
          { name: "Horizon City", route: "/el-paso" }, { name: "Socorro", route: "/el-paso" },
          { name: "Anthony", route: "/el-paso" }, { name: "Fabens", route: "/el-paso" },
          { name: "Clint", route: "/el-paso" }, { name: "Fort Bliss", route: "/el-paso" },
          { name: "Las Cruces NM area", route: "/el-paso" }, { name: "Santa Teresa NM", route: "/el-paso" },
        ],
      },
      {
        name: "Permian Basin — Midland & Odessa",
        description: "The Permian Basin is an energy hub with frequent international travel needs. Midland International Air & Space Port serves both cities; direct flights to international destinations are common for oil industry professionals.",
        hub: { name: "Midland", route: "/midland" },
        communities: [
          { name: "Stanton", route: "/midland" }, { name: "Andrews", route: "/midland" },
          { name: "Crane", route: "/midland" }, { name: "Monahans", route: "/midland" },
          { name: "Pecos", route: "/odessa" }, { name: "Fort Stockton", route: "/odessa" },
          { name: "Big Spring", route: "/midland" }, { name: "Snyder", route: "/midland" },
        ],
      },
    ],
  },
  {
    id: "south-gulf",
    name: "South Texas & Gulf Coast",
    icon: "ri-ship-line",
    subregions: [
      {
        name: "Rio Grande Valley",
        description: "McAllen, Edinburg, Harlingen, and Brownsville form the densely populated RGV. The Anzalduas and Pharr-Reynosa bridges mean frequent cross-border travel — and frequent passport urgency — for valley residents.",
        hub: { name: "McAllen", route: "/mcallen" },
        communities: [
          { name: "Edinburg", route: "/mcallen" }, { name: "Mission", route: "/mcallen" },
          { name: "Pharr", route: "/mcallen" }, { name: "San Juan", route: "/mcallen" },
          { name: "Weslaco", route: "/mcallen" }, { name: "Harlingen", route: "/mcallen" },
          { name: "Los Fresnos", route: "/brownsville" }, { name: "San Benito", route: "/brownsville" },
          { name: "Donna", route: "/mcallen" }, { name: "Alamo", route: "/mcallen" },
        ],
      },
      {
        name: "Corpus Christi & Coastal Bend",
        description: "Corpus Christi Bay communities, Portland, and the Padre Island area. Port of Corpus Christi sees significant international shipping. Naval Air Station Corpus Christi means a steady stream of military passport applications.",
        hub: { name: "Corpus Christi", route: "/corpus-christi" },
        communities: [
          { name: "Portland", route: "/corpus-christi" }, { name: "Robstown", route: "/corpus-christi" },
          { name: "Flour Bluff", route: "/corpus-christi" }, { name: "Aransas Pass", route: "/corpus-christi" },
          { name: "Ingleside", route: "/corpus-christi" }, { name: "Rockport", route: "/corpus-christi" },
          { name: "Victoria", route: "/corpus-christi" }, { name: "Laredo", route: "/laredo" },
        ],
      },
    ],
  },
  {
    id: "east-north-texas",
    name: "East & North Texas",
    icon: "ri-tree-line",
    subregions: [
      {
        name: "East Texas Piney Woods",
        description: "Tyler is the hub of the Piney Woods region — Longview, Marshall, and Lufkin all route through here. East Texas Regional Airport offers connections, but most travelers drive to DFW or IAH for international departures.",
        hub: { name: "Tyler", route: "/tyler" },
        communities: [
          { name: "Longview", route: "/tyler" }, { name: "Whitehouse", route: "/tyler" },
          { name: "Lindale", route: "/tyler" }, { name: "Marshall", route: "/tyler" },
          { name: "Nacogdoches", route: "/tyler" }, { name: "Lufkin", route: "/tyler" },
          { name: "Kilgore", route: "/tyler" }, { name: "Henderson", route: "/tyler" },
        ],
      },
      {
        name: "North Texas — Wichita Falls & Abilene",
        description: "Northwestern Texas cities served from Wichita Falls and Abilene hubs. Sheppard AFB (Wichita Falls) and Dyess AFB (Abilene) mean substantial active-duty populations needing expedited military passport processing.",
        hub: { name: "Wichita Falls", route: "/wichita-falls" },
        communities: [
          { name: "Burkburnett", route: "/wichita-falls" }, { name: "Iowa Park", route: "/wichita-falls" },
          { name: "Bowie", route: "/wichita-falls" }, { name: "Vernon", route: "/wichita-falls" },
          { name: "Sweetwater", route: "/abilene" }, { name: "San Angelo", route: "/san-angelo" },
          { name: "Stephenville", route: "/abilene" }, { name: "Mineral Wells", route: "/fort-worth" },
        ],
      },
    ],
  },
];

export default function TexasCommunitiesWeServe() {
  const [activeRegion, setActiveRegion] = useState(regions[0].id);

  const current = regions.find((r) => r.id === activeRegion) || regions[0];

  return (
    <section id="communities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Statewide Coverage
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Communities We Serve Across Texas
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Don&apos;t see your city in the directory above? You&apos;re still covered. Every Texas community below is served through our nearest hub — with the same fast, reliable expedited passport processing.
          </p>
        </div>

        {/* Region tab selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {regions.map((region) => (
            <button
              key={region.id}
              onClick={() => setActiveRegion(region.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeRegion === region.id
                  ? "bg-[#1A365D] text-white"
                  : "bg-[#EEF2F8] text-[#1A365D] hover:bg-[#dce5f3]"
              }`}
            >
              <i className={`${region.icon} text-base`}></i>
              {region.name}
            </button>
          ))}
        </div>

        {/* Subregion cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {current.subregions.map((sub, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-100">
              <div className="flex items-start justify-between gap-4 mb-3">
                <h3 className="font-bold text-[#1A365D] text-base">{sub.name}</h3>
                <a
                  href={sub.hub.route}
                  className="flex-shrink-0 inline-flex items-center gap-1 bg-[#1A365D] text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-[#F6C344] hover:text-[#1A365D] transition-all whitespace-nowrap"
                >
                  Hub: {sub.hub.name} <i className="ri-arrow-right-line"></i>
                </a>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{sub.description}</p>
              <div className="flex flex-wrap gap-2">
                {sub.communities.map((c, j) => (
                  <a
                    key={j}
                    href={c.route}
                    className="inline-flex items-center text-xs font-medium text-[#1A365D] bg-white border border-gray-200 rounded-full px-3 py-1 hover:border-[#F6C344] hover:text-[#1A365D] transition-all whitespace-nowrap cursor-pointer"
                  >
                    {c.name}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-sm mt-8">
          Not finding your exact community? <a href="tel:8772530084" className="text-[#1A365D] font-semibold hover:text-[#F6C344] transition-colors">Call us at 877-253-0084</a> — we serve all of Texas.
        </p>
      </div>
    </section>
  );
}